import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, bg, formatApiError } from "@/lib/api";
import { UserAvatar, roleLabel } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Loader2, Plus, Search, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

function when(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

// separador de dia na conversa: "Hoje", "Ontem" ou a data
function dia(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const hoje = new Date(); const ontem = new Date(); ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === hoje.toDateString()) return "Hoje";
  if (d.toDateString() === ontem.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: d.getFullYear() === hoje.getFullYear() ? undefined : "numeric" });
}

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const [convs, setConvs] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [picking, setPicking] = useState(false);
  const [filter, setFilter] = useState("");
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConvs = () => api.get("/messages/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([]));

  useEffect(() => { loadConvs(); }, []);

  useEffect(() => {
    if (!activeId) { setThread(null); return; }
    let alive = true;
    const load = (first) =>
      api.get(`/messages/${activeId}`, first ? undefined : bg)
        .then((r) => {
          if (!alive) return;
          setThread(r.data);
          if (first) { loadConvs(); window.dispatchEvent(new Event("iep:messages-read")); }
        })
        .catch((e) => { if (first) { toast.error(formatApiError(e.response?.data?.detail)); setParams({}); } });
    setThread(null);
    load(true);
    const t = setInterval(() => load(false), 10000);
    return () => { alive = false; clearInterval(t); };
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [thread?.messages?.length]);

  const openPicker = () => {
    setPicking(true);
    setFilter("");
    if (!contacts) api.get("/messages/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([]));
  };

  const shownContacts = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (contacts || []).filter((c) => !f || (c.name || "").toLowerCase().includes(f));
  }, [contacts, filter]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || !activeId) return;
    setSending(true);
    try {
      const r = await api.post(`/messages/${activeId}`, { body });
      setThread((t) => ({ ...t, messages: [...(t?.messages || []), r.data] }));
      setText("");
      loadConvs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const open = (id) => { setPicking(false); setParams({ c: id }); };

  return (
    <div className="max-w-5xl mx-auto w-full animate-fade-up">
      <h1 className="font-display text-2xl font-bold text-slate-900">Mensagens</h1>
      <p className="text-slate-500 mt-1 mb-5 text-sm">Fale com a escola e com a sua professora. As respostas chegam aqui e no sininho.</p>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden grid md:grid-cols-[320px_1fr] h-[calc(100vh-14rem)] min-h-[440px]">
        {/* Lista */}
        <aside className={`border-r border-slate-200 flex flex-col min-h-0 ${activeId ? "hidden md:flex" : "flex"}`}>
          <div className="p-3 border-b border-slate-200">
            <Button onClick={openPicker} className="w-full text-white" style={{ background: "#1E9E2A" }} data-testid="messages-new-button">
              <Plus className="h-4 w-4 mr-1.5" /> Nova mensagem
            </Button>
          </div>
          {picking ? (
            <div className="flex flex-col min-h-0 flex-1">
              <div className="p-3 flex gap-2 items-center">
                <button onClick={() => setPicking(false)} className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /></button>
                <div className="relative flex-1">
                  <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input autoFocus value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar contato" className="pl-8 h-9" />
                </div>
              </div>
              <div className="overflow-y-auto iep-scroll flex-1">
                {contacts === null && <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
                {contacts && shownContacts.length === 0 && <p className="p-4 text-sm text-slate-400">Nenhum contato disponível.</p>}
                {shownContacts.map((c) => (
                  <button key={c.id} onClick={() => open(c.id)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                    <UserAvatar user={c} size={36} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
                      <div className="text-xs text-slate-400">{roleLabel(c)}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto iep-scroll flex-1">
              {convs === null && <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
              {convs && convs.length === 0 && (
                <div className="p-6 text-center">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-green-50 text-[#1E9E2A]"><MessageSquare className="h-6 w-6" /></div>
                  <p className="mt-3 text-sm font-semibold text-slate-700">Nenhuma conversa ainda</p>
                  <p className="mt-1 text-xs text-slate-500">Toque em <b>Nova mensagem</b> para falar com a escola ou com a sua professora.</p>
                </div>
              )}
              {(convs || []).map((c) => (
                <button
                  key={c.contact.id}
                  onClick={() => open(c.contact.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-l-4 transition-colors ${activeId === c.contact.id ? "bg-green-50/70 border-[#1E9E2A]" : "border-transparent hover:bg-slate-50"}`}
                >
                  <UserAvatar user={c.contact} size={40} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800 truncate flex-1">{c.contact.name}</span>
                      <span className="text-[11px] text-slate-400 shrink-0">{when(c.last.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs truncate flex-1 ${c.unread ? "text-slate-800 font-semibold" : "text-slate-500"}`}>
                        {c.last.from_me ? "Você: " : ""}{c.last.body}
                      </span>
                      {c.unread > 0 && <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">{c.unread}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Conversa */}
        <section className={`flex flex-col min-h-0 ${activeId ? "flex" : "hidden md:flex"}`}>
          {!activeId ? (
            <div className="flex-1 grid place-items-center text-center p-8 bg-gradient-to-b from-white to-[#F6F8FB]">
              <div className="max-w-xs">
                <div className="mx-auto grid h-20 w-20 place-items-center rounded-3xl text-white shadow-[0_14px_30px_-12px_rgba(26,35,64,.6)]" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 55%,#1E9E2A)" }}>
                  <MessageSquare className="h-9 w-9" />
                </div>
                <p className="font-display text-lg font-bold text-[#1A2340] mt-5">Suas mensagens</p>
                <p className="text-slate-500 mt-1.5 text-sm">Escolha uma conversa ao lado ou comece uma nova para tirar dúvidas com a escola e com a sua professora.</p>
                <Button onClick={openPicker} className="mt-5 text-white" style={{ background: "#1E9E2A" }}><Plus className="h-4 w-4 mr-1.5" /> Nova mensagem</Button>
              </div>
            </div>
          ) : !thread ? (
            <div className="flex-1 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
                <button className="md:hidden text-slate-400" onClick={() => setParams({})}><ArrowLeft className="h-5 w-5" /></button>
                <UserAvatar user={thread.contact} size={36} />
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{thread.contact.name}</div>
                  <div className="text-xs text-slate-400">{roleLabel(thread.contact)}</div>
                </div>
              </header>
              <div className="flex-1 overflow-y-auto iep-scroll p-4 space-y-2 bg-[#F3F6FA]">
                {thread.messages.length === 0 && <p className="text-center text-sm text-slate-400 mt-8">Envie a primeira mensagem.</p>}
                {thread.messages.map((m, i) => {
                  const mine = m.from_id !== thread.contact.id;
                  const novoDia = i === 0 || dia(m.created_at) !== dia(thread.messages[i - 1].created_at);
                  return (
                    <React.Fragment key={m.id}>
                      {novoDia && <div className="flex justify-center py-2"><span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-slate-500 shadow-sm ring-1 ring-slate-200/70">{dia(m.created_at)}</span></div>}
                      <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[14.5px] leading-relaxed whitespace-pre-wrap break-words shadow-sm ${mine ? "text-white rounded-br-md" : "bg-white ring-1 ring-slate-200/80 text-slate-800 rounded-bl-md"}`}
                          style={mine ? { background: "linear-gradient(135deg,#232D4F,#2c3a66)" } : undefined}>
                          {m.body}
                          <div className={`text-[10px] mt-1 text-right ${mine ? "text-white/60" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2 items-end">
                <Textarea
                  rows={1}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
                  placeholder="Escreva sua mensagem…"
                  className="min-h-[44px] max-h-32 resize-none"
                  data-testid="messages-input"
                />
                <Button type="submit" disabled={sending || !text.trim()} className="h-11 text-white" style={{ background: "#1E9E2A" }} data-testid="messages-send-button">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
